import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Check, MessageSquare, BadgeCheck, Loader2, Trophy } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { customerMenu } from "@/config/menus";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Stars, VerifiedBadge, StatusPill, inr, LoadingScreen, EmptyState } from "@/components/common";

export default function RequirementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [match, setMatch] = useState(null);
  const [matching, setMatching] = useState(false);
  const [accepting, setAccepting] = useState("");

  const load = async () => {
    const [r, q] = await Promise.all([api.get(`/requirements/${id}`), api.get(`/requirements/${id}/quotes`)]);
    setReq(r.data); setQuotes(q.data);
  };
  useEffect(() => { load().catch(() => {}); }, [id]);

  const runMatch = async () => {
    setMatching(true);
    try { const { data } = await api.post(`/requirements/${id}/match`); setMatch(data); }
    catch (e) { toast.error("Matching failed"); } finally { setMatching(false); }
  };

  const accept = async (q) => {
    setAccepting(q.id);
    try {
      const { data } = await api.post(`/quotes/${q.id}/accept`);
      toast.success("Quotation accepted! Contract generated.");
      navigate(`/contracts/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to accept"); } finally { setAccepting(""); }
  };

  if (!req) return <DashboardShell menu={customerMenu} title="Requirement"><LoadingScreen /></DashboardShell>;
  const accepted = quotes.find((q) => q.status === "ACCEPTED");

  return (
    <DashboardShell menu={customerMenu} title="Quotation Marketplace">
      <button onClick={() => navigate("/requirements")} className="flex items-center gap-1 text-sm text-navy-500 hover:text-brand mb-4"><ArrowLeft size={16} /> All requirements</button>

      <div className="bg-navy-900 text-white rounded-xl p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-heading text-2xl font-bold">{req.security_requirement}</h2>
            <p className="text-navy-300 mt-1">{req.num_guards} guards · {req.location} · {req.shift_duration}h × {req.num_shifts} shift(s) · {req.contract_duration_months} months</p>
          </div>
          <StatusPill status={req.status} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {req.supervisor_required && <Badge className="bg-white/10">Supervisor</Badge>}
          {req.cctv_required && <Badge className="bg-white/10">CCTV</Badge>}
          {req.patrolling_required && <Badge className="bg-white/10">Patrolling</Badge>}
          <Badge className="bg-white/10">{req.armed}</Badge>
          {req.budget_range && <Badge className="bg-white/10">Budget: {req.budget_range}</Badge>}
        </div>
      </div>

      {/* AI Matching */}
      <div className="bg-white border border-navy-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-brand text-white flex items-center justify-center"><Sparkles size={18} /></div>
            <div><h3 className="font-heading font-semibold text-navy-900">AI Agency Matching</h3><p className="text-sm text-navy-500">Transparent, admin-configurable scoring based on your needs.</p></div>
          </div>
          <Button onClick={runMatch} disabled={matching} className="bg-navy-900 hover:bg-navy-800" data-testid="run-match-btn">
            {matching ? <><Loader2 size={16} className="mr-1 animate-spin" /> Analysing…</> : <><Sparkles size={16} className="mr-1" /> Get Recommendations</>}
          </Button>
        </div>
        {match && (
          <div className="mt-5">
            {match.ai_summary && <div className="bg-brand-50 border border-brand-100 rounded-lg p-4 text-sm text-navy-700 mb-4"><span className="font-semibold text-brand-600">AI Advisor: </span>{match.ai_summary}</div>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {match.recommended.map((a, i) => (
                <div key={a.id} className="border border-navy-200 rounded-lg p-4" data-testid={`match-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-navy-900 text-sm">{a.name}</span>
                    {i === 0 && <Trophy size={16} className="text-amber-500" />}
                  </div>
                  <div className="flex items-center gap-2 mt-1"><div className="flex-1 h-2 bg-navy-100 rounded-full overflow-hidden"><div className="h-full bg-brand" style={{ width: `${a.match_score}%` }} /></div><span className="text-xs font-bold text-navy-700">{a.match_score}</span></div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-navy-500"><Stars value={a.rating} size={11} /> {a.years_experience}y · {inr(a.price_per_guard_month)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quotations */}
      <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">Quotations received ({quotes.length})</h3>
      {quotes.length === 0 ? (
        <EmptyState icon={MessageSquare} title="Awaiting quotations" hint="Verified agencies in your area have been notified. Quotations will appear here shortly." />
      ) : (
        <div className="overflow-x-auto bg-white border border-navy-200 rounded-xl">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Agency</th><th className="p-3">Rating</th><th className="p-3">Exp</th>
                <th className="p-3">Manpower</th><th className="p-3">Price/guard</th><th className="p-3">Total/month</th>
                <th className="p-3">SLA</th><th className="p-3">Supervisor</th><th className="p-3">Status</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} className="border-t border-navy-100" data-testid={`quote-row-${q.id}`}>
                  <td className="p-3">
                    <div className="font-medium text-navy-900 flex items-center gap-1">{q.agency_name} {q.agency_verified && <BadgeCheck size={14} className="text-emerald-500" />}</div>
                  </td>
                  <td className="p-3 text-center"><Stars value={q.agency_rating} size={11} /></td>
                  <td className="p-3 text-center text-navy-600">{q.agency_experience}y</td>
                  <td className="p-3 text-center text-navy-600">{q.available_manpower}</td>
                  <td className="p-3 text-center text-navy-900 font-semibold">{inr(q.price_per_guard_month)}</td>
                  <td className="p-3 text-center text-navy-900 font-bold">{inr(q.total_monthly)}</td>
                  <td className="p-3 text-center text-navy-600">{q.replacement_sla_hours}h</td>
                  <td className="p-3 text-center">{q.supervisor_included ? <Check size={16} className="text-emerald-500 mx-auto" /> : "—"}</td>
                  <td className="p-3 text-center"><StatusPill status={q.status} /></td>
                  <td className="p-3 text-right">
                    {!accepted && q.status === "SUBMITTED" && (
                      <Button size="sm" className="bg-brand hover:bg-brand-600" disabled={accepting === q.id} onClick={() => accept(q)} data-testid={`accept-${q.id}`}>
                        {accepting === q.id ? <Loader2 size={14} className="animate-spin" /> : "Accept"}
                      </Button>
                    )}
                    {q.status === "ACCEPTED" && <Badge className="bg-emerald-500 hover:bg-emerald-500">Accepted</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
